import React, { useState, useEffect } from 'react';
import MuiGrid from '../components/MuiGrid';
import { useParams, useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button,
  CircularProgress,
  Avatar,
  Card,
  CardContent,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  LinearProgress,
  Stack,
  Pagination,
  TableFooter,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton
} from '@mui/material';
import { supabase } from '../services/supabase';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupIcon from '@mui/icons-material/Group';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SortIcon from '@mui/icons-material/Sort';
import ClearIcon from '@mui/icons-material/Clear';
import type { Player } from '../types/models';

const PlayerProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState<Player | null>(null);
  const [playerStats, setPlayerStats] = useState<any | null>(null);
  const [recentGames, setRecentGames] = useState<any[]>([]);
  const [allGames, setAllGames] = useState<any[]>([]);
  const [filteredGames, setFilteredGames] = useState<any[]>([]);
  const [judgedGames, setJudgedGames] = useState<any[]>([]);
  const [gameResults, setGameResults] = useState<{[gameId: string]: any[]}>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const gamesPerPage = 10;
  const [availableLeagues, setAvailableLeagues] = useState<{id: string, name: string}[]>([]);
  const [leagueFilter, setLeagueFilter] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchPlayerData(id);
    }
  }, [id]);
  
  // Handle URL parameters
  useEffect(() => {
    const leagueId = searchParams.get('league');
    if (leagueId) {
      setLeagueFilter(leagueId);
    }
  }, [searchParams]);
  
  // Apply filters when filter conditions or all games change
  useEffect(() => {
    if (allGames.length > 0) {
      let filtered = [...allGames];
      
      // Apply league filter if selected
      if (leagueFilter) {
        filtered = filtered.filter(game => 
          game.game?.league?.id === leagueFilter
        );
      }
      
      // Sort by date descending (most recent first)
      filtered = filtered.sort((a, b) => {
        const dateA = new Date(a.game.occurred_at).getTime();
        const dateB = new Date(b.game.occurred_at).getTime();
        return dateB - dateA;
      });
      
      setFilteredGames(filtered);
      setTotalPages(Math.ceil(filtered.length / gamesPerPage));
      setPage(1); // Reset to first page when filters change
    }
  }, [allGames, leagueFilter]);
  
  // Fetch the game results for each game to properly check winning moves
  useEffect(() => {
    if (recentGames.length > 0) {
      fetchGameResults();
    }
  }, [recentGames]);
  
  // Update displayed games when page changes or filtered games are updated
  useEffect(() => {
    if (filteredGames.length > 0) {
      const startIndex = (page - 1) * gamesPerPage;
      const endIndex = startIndex + gamesPerPage;
      setRecentGames(filteredGames.slice(startIndex, endIndex));
    } else {
      setRecentGames([]);
    }
  }, [page, filteredGames]);
  
  const fetchGameResults = async () => {
    const gameIds = recentGames.map(game => game.game_id);
    const uniqueGameIds = Array.from(new Set(gameIds));
    
    try {
      const { data, error } = await supabase
        .from('game_results')
        .select('*')
        .in('game_id', uniqueGameIds);
        
      if (error) throw error;
      
      // Group results by game_id
      const results: {[gameId: string]: any[]} = {};
      (data || []).forEach(result => {
        if (!results[result.game_id]) {
          results[result.game_id] = [];
        }
        results[result.game_id].push(result);
      });
      
      setGameResults(results);
    } catch (error: any) {
      console.error('Error fetching game results:', error.message);
    }
  };

  const fetchPlayerData = async (playerId: string) => {
    setLoading(true);
    try {
      // Fetch player details
      const { data: playerData, error: playerError } = await supabase
        .from('players')
        .select('*')
        .eq('id', playerId)
        .single();

      if (playerError) throw playerError;
      setPlayer(playerData);
      
      // If player is a judge, fetch the games they judged and leagues they're assigned to
      if (playerData.is_judge) {
        console.log(`Fetching data for judge: ${playerData.nickname} (${playerId})`);
        
        // Fetch judged games
        const { data: judgingData, error: judgingError } = await supabase
          .from('games')
          .select(`
            id,
            session_num,
            number_in_session,
            occurred_at,
            winner,
            league:league_id (
              id,
              name
            )
          `)
          .eq('judge_id', playerId)
          .order('occurred_at', { ascending: false });
          
        if (judgingError) throw judgingError;
        
        console.log(`Found ${judgingData?.length || 0} games judged by this player`);
        setJudgedGames(judgingData || []);
        
        // Also fetch leagues where this judge is assigned as the primary judge
        console.log(`Fetching leagues where ${playerData.nickname} is the primary judge`);
        const { data: leaguesData, error: leaguesError } = await supabase
          .from('leagues')
          .select(`
            id,
            name,
            club:club_id (
              id,
              name
            )
          `)
          .eq('judge_id', playerId)
          .order('name', { ascending: true });
        
        if (leaguesError) {
          console.error('Error fetching leagues:', leaguesError.message);
          throw leaguesError;
        }
        
        console.log(`Found ${leaguesData?.length || 0} leagues with this judge assigned`);
        if (leaguesData && leaguesData.length > 0) {
          console.log('Leagues found:', leaguesData);
        }
        
        // Store leagues data in judgedGames state with a special property to differentiate
        if (leaguesData && leaguesData.length > 0) {
          const leaguesWithMarker = leaguesData.map(league => {
            // Add a special property to indicate this is a league, not a game
            return { ...league, isLeague: true };
          });
          
          // Add the leagues to the judged games array
          setJudgedGames([...judgingData || [], ...leaguesWithMarker]);
          console.log(`Found ${leaguesWithMarker.length} leagues where player is the judge`);
        }
        
        setLoading(false);
        return; // Skip regular player stats if this is a judge
      }
      
      // For regular players, fetch player statistics
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_player_stats', { player_id_param: playerId } as any);

      if (statsError) {
        console.error('Player stats function not found, using fallback query');
        // Fallback query if the function doesn't exist
        const { data: resultsData, error: resultsError } = await supabase
          .from('game_results')
          .select(`
            *,
            game:game_id (
              id,
              session_num,
              number_in_session,
              occurred_at,
              winner,
              league:league_id (
                id,
                name
              )
            )
          `)
          .eq('player_id', playerId);

        if (resultsError) throw resultsError;

        // Calculate stats manually
        const gamesPlayed = resultsData?.length || 0;
        const gamesWon = resultsData?.filter(r => r.is_win).length || 0;
        const winRate = gamesPlayed > 0 ? ((gamesWon / gamesPlayed) * 100).toFixed(1) : '0';
        
        const civilianGames = resultsData?.filter(r => r.role === 'CITIZEN').length || 0;
        const civilianWins = resultsData?.filter(r => r.role === 'CITIZEN' && r.is_win).length || 0;
        
        const mafiaGames = resultsData?.filter(r => r.role === 'MAFIA').length || 0;
        const mafiaWins = resultsData?.filter(r => r.role === 'MAFIA' && r.is_win).length || 0;
        
        const sheriffGames = resultsData?.filter(r => r.role === 'SHERIFF').length || 0;
        const sheriffWins = resultsData?.filter(r => r.role === 'SHERIFF' && r.is_win).length || 0;
        
        const donGames = resultsData?.filter(r => r.role === 'DON').length || 0;
        const donWins = resultsData?.filter(r => r.role === 'DON' && r.is_win).length || 0;
        
        const timesFirstKilled = resultsData?.filter(r => r.is_killed_first).length || 0;
        
        const totalBonusPoints = resultsData?.reduce((sum, r) => sum + (r.bonus_points || 0), 0) || 0;
        const avgBonusPoints = gamesPlayed > 0 ? (totalBonusPoints / gamesPlayed).toFixed(2) : '0';

        setPlayerStats({
          games_played: gamesPlayed,
          games_won: gamesWon,
          win_rate: winRate,
          civilian_games: civilianGames,
          civilian_wins: civilianWins,
          mafia_games: mafiaGames,
          mafia_wins: mafiaWins,
          sheriff_games: sheriffGames,
          sheriff_wins: sheriffWins,
          don_games: donGames,
          don_wins: donWins,
          times_first_killed: timesFirstKilled,
          avg_bonus_points: avgBonusPoints
        });

        // Sort games by session number and game number in descending order
        const sortedGames = [...resultsData].sort((a, b) => {
          // First sort by session number (descending)
          if (a.game.session_num !== b.game.session_num) {
            return b.game.session_num - a.game.session_num;
          }
          // Then sort by game number within session (descending)
          return b.game.number_in_session - a.game.number_in_session;
        });

        setAllGames(sortedGames);
        setTotalPages(Math.ceil(sortedGames.length / gamesPerPage));
        // Initial page of games
        setRecentGames(sortedGames.slice(0, gamesPerPage));
      } else {
        setPlayerStats(statsData[0]);

        // Fetch all games to support pagination
        const { data: allGamesData, error: allGamesError } = await supabase
          .from('game_results')
          .select(`
            *,
            game:game_id (
              id,
              session_num,
              number_in_session,
              occurred_at,
              winner,
              league:league_id (
                id,
                name
              )
            )
          `)
          .eq('player_id', playerId)
          .order('created_at', { ascending: false });  // Order by creation date as fallback

        if (allGamesError) {
          console.error('Error fetching games:', allGamesError);
          throw allGamesError;
        }
        
        console.log(`Found ${allGamesData?.length || 0} games for player ${playerId}`);
        
        // Sort by session and game number
        const sortedGames = (allGamesData || []).sort((a, b) => {
          if (a.game.session_num !== b.game.session_num) {
            return b.game.session_num - a.game.session_num;
          }
          return b.game.number_in_session - a.game.number_in_session;
        });
        
        setAllGames(sortedGames);
        
        // Extract unique leagues from games
        const leaguesMap = new Map();
        sortedGames.forEach(game => {
          if (game.game?.league?.id && game.game?.league?.name) {
            leaguesMap.set(game.game.league.id, {
              id: game.game.league.id,
              name: game.game.league.name
            });
          }
        });
        
        // Convert Map to Array for the dropdown
        const leaguesList = Array.from(leaguesMap.values());
        setAvailableLeagues(leaguesList);
        
        // Initial filtering will be done by the useEffect
      }
    } catch (error: any) {
      console.error('Error fetching player data:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  
  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };
  
  const handleLeagueFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const leagueId = event.target.value as string;
    setLeagueFilter(leagueId);
    
    // Update URL parameters
    if (leagueId) {
      setSearchParams({ league: leagueId });
    } else {
      // Remove league parameter if no filter is selected
      searchParams.delete('league');
      setSearchParams(searchParams);
    }
  };
  
  const clearFilters = () => {
    setLeagueFilter("");
    searchParams.delete('league');
    setSearchParams(searchParams);
  };
  
  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };
  
  const getWinningMoveBonus = (result: any) => {
    // Only show for first-killed citizens or sheriff with a winning move
    if (!result.is_killed_first || !result.winning_move || 
        (result.role !== 'CITIZEN' && result.role !== 'SHERIFF')) {
      return null;
    }
    
    try {
      // Parse the winning move string to get the guessed mafia numbers
      let guessedMafiaNumbers = [];
      if (result.winning_move.includes(',')) {
        guessedMafiaNumbers = result.winning_move.split(',').map((n: string) => parseInt(n.trim(), 10));
      } else if (result.winning_move.includes(' ')) {
        guessedMafiaNumbers = result.winning_move.split(' ').map((n: string) => parseInt(n.trim(), 10));
      } else {
        guessedMafiaNumbers = result.winning_move.match(/\d+/g)?.map((n: string) => parseInt(n, 10)) || [];
      }
      
      // Get all players in this game from our gameResults state
      const gamePlayersResults = gameResults[result.game_id] || [];
      
      if (gamePlayersResults.length === 0) {
        // If we don't have the game results yet, show a loading state
        return (
          <Chip 
            icon={<GpsFixedIcon />}
            label={`${guessedMafiaNumbers.length}`}
            size="small"
            color="default"
            sx={{ mt: 0.5 }}
          />
        );
      }
      
      // Find actual mafia players in this game
      const mafiaPlayers = gamePlayersResults.filter(gr => 
        gr.role === 'MAFIA' || gr.role === 'DON'
      );
      
      // Count how many guesses were correct
      const correctGuesses = guessedMafiaNumbers.filter((playerNum: number) => 
        mafiaPlayers.some(mp => mp.player_num === playerNum)
      ).length;
      
      // Based on the number of correct guesses, return the appropriate chip
      if (correctGuesses === 2) {
        return (
          <Chip 
            icon={<GpsFixedIcon />}
            label="2 | +0.5"
            size="small"
            color="secondary"
            sx={{ mt: 0.5 }}
          />
        );
      } else if (correctGuesses === 3) {
        return (
          <Chip 
            icon={<GpsFixedIcon />}
            label="3 | +0.8"
            size="small"
            color="secondary"
            sx={{ mt: 0.5 }}
          />
        );
      } else if (correctGuesses > 0) {
        // If there were some correct guesses but not enough for a bonus
        return
      }
    } catch (e) {
      console.error('Error processing winning move:', e);
    }
    
    return null;
  };

  const getRoleChip = (role: string) => {
    switch (role) {
      case 'CITIZEN':
        return <Chip icon={<PeopleIcon />} label="CITIZEN" color="primary" size="small" />;
      case 'MAFIA':
        return <Chip icon={<SportsKabaddiIcon />} label="MAFIA" color="error" size="small" />;
      case 'SHERIFF':
        return <Chip icon={<LocalPoliceIcon />} label="SHERIFF" color="success" size="small" />;
      case 'DON':
        return <Chip icon={<TheaterComedyIcon />} label="DON" color="warning" size="small" />;
      default:
        return null;
    }
  };
  
  const getGameWinnerChip = (winner: string) => {
    switch (winner) {
      case 'CIVILIANS':
        return <Chip icon={<GroupIcon />} label="CIVILIANS" color="primary" size="small" />;
      case 'MAFIA':
        return <Chip icon={<SportsKabaddiIcon />} label="MAFIA" color="error" size="small" />;
      case 'TIE':
        return <Chip label="TIE" variant="outlined" size="small" />;
      default:
        return null;
    }
  };

  const getResultChip = (isWin: boolean) => {
    return isWin ? 
      <Chip icon={<EmojiEventsIcon />} label="WIN" color="success" size="small" /> : 
      <Chip label="LOSS" variant="outlined" size="small" />;
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };
  
  const getPlayerAvatar = (player: Player) => {
    if (player.profile_picture_url) {
      return (
        <Avatar 
          src={player.profile_picture_url} 
          alt={player.nickname}
          sx={{ 
            width: 80, 
            height: 80, 
            mr: 3 
          }}
        />
      );
    }
    
    return (
      <Avatar 
        sx={{ 
          width: 80, 
          height: 80, 
          bgcolor: 'primary.main',
          fontSize: '1.8rem',
          mr: 3
        }}
      >
        {getInitials(player.nickname)}
      </Avatar>
    );
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Button
          component={RouterLink}
          to="/players"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
          variant="outlined"
        >
          Back to All Players
        </Button>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {player ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  {getPlayerAvatar(player)}
                  <Box>
                    <Typography variant="h4" component="h1">
                      {player.nickname}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {player.full_name}
                    </Typography>
                    {player.is_judge && (
                      <Chip label="Judge" color="secondary" size="small" sx={{ mt: 1 }} />
                    )}
                  </Box>
                </Box>
                
                {/* For judges, show judged games */}
                {player && player.is_judge ? (
                  <MuiGrid container spacing={3} sx={{ mt: 3 }}>
                    {/* Judge's Leagues Section */}
                    <MuiGrid item xs={12} md={6}>
                      <Paper sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <GavelIcon sx={{ mr: 1, color: 'secondary.main' }} />
                          <Typography variant="h6">
                            Judge For Leagues
                          </Typography>
                        </Box>
                        
                        {judgedGames.filter((item: any) => item.isLeague === true).length > 0 ? (
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>League</TableCell>
                                  <TableCell>Club</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {judgedGames
                                  .filter((item: any) => item.isLeague === true)
                                  .map((league: any) => (
                                    <TableRow 
                                      key={league.id}
                                      hover
                                      onClick={() => navigate(`/league/${league.id}`)}
                                      sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': {
                                          backgroundColor: 'rgba(255, 255, 255, 0.08)'
                                        }
                                      }}
                                    >
                                      <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                          {league.name}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        {/* Access club property correctly based on how it's fetched from Supabase */}
                                        {league.club ? 
                                          (typeof league.club === 'string' ? league.club : league.club.name) 
                                          : 'Unknown'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not assigned as primary judge for any leagues.
                          </Typography>
                        )}
                      </Paper>
                    </MuiGrid>
                    
                    {/* Judged Games Section */}
                    <MuiGrid item xs={12} md={6}>
                      <Paper sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <GavelIcon sx={{ mr: 1, color: 'secondary.main' }} />
                          <Typography variant="h6">
                            Judged Games
                          </Typography>
                        </Box>
                        
                        {judgedGames.filter((item: any) => item.isLeague !== true).length > 0 ? (
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Game</TableCell>
                                  <TableCell>League</TableCell>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Winner</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {judgedGames
                                  .filter((item: any) => item.isLeague !== true)
                                  .map((game) => (
                                    <TableRow 
                                      key={game.id}
                                      hover
                                      onClick={() => navigate(`/games?game=${game.id}`)}
                                      sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': {
                                          backgroundColor: 'rgba(255, 255, 255, 0.08)'
                                        }
                                      }}
                                    >
                                      <TableCell>
                                        <Typography variant="body2">
                                          Session {game.session_num}, Game {game.number_in_session}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        {(game.league as any)?.name || 'Unknown'}
                                      </TableCell>
                                      <TableCell>
                                        {formatDate(game.occurred_at)}
                                      </TableCell>
                                      <TableCell>
                                        {getGameWinnerChip(game.winner)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No judged games found.
                          </Typography>
                        )}
                      </Paper>
                    </MuiGrid>
                  </MuiGrid>
                ) : (
                  // For regular players, show stats
                  playerStats && (
                    <>
                      <MuiGrid container spacing={3} sx={{ mb: 4 }}>
                        <MuiGrid item xs={12} sm={6} md={3}>
                          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                                Games Played
                              </Typography>
                              <Typography variant="h4" align="center" sx={{ mt: 'auto', mb: 'auto' }}>
                                {playerStats.games_played}
                              </Typography>
                            </CardContent>
                          </Card>
                        </MuiGrid>
                        
                        <MuiGrid item xs={12} sm={6} md={3}>
                          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                                Games Won
                              </Typography>
                              <Typography variant="h4" color="success.main" align="center" sx={{ mt: 'auto', mb: 'auto' }}>
                                {playerStats.games_won}
                              </Typography>
                            </CardContent>
                          </Card>
                        </MuiGrid>
                        
                        <MuiGrid item xs={12} sm={6} md={3}>
                          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                                Win Rate
                              </Typography>
                              <Typography variant="h4" align="center">
                                {playerStats.win_rate}%
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={Number(playerStats.win_rate)} 
                                sx={{ mt: 2 }}
                              />
                            </CardContent>
                          </Card>
                        </MuiGrid>
                        
                        <MuiGrid item xs={12} sm={6} md={3}>
                          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                                First Kills
                              </Typography>
                              <Typography variant="h4" color="error.main" align="center" sx={{ mt: 'auto', mb: 'auto' }}>
                                {playerStats.times_first_killed}
                              </Typography>
                            </CardContent>
                          </Card>
                        </MuiGrid>
                      </MuiGrid>
                      
                      <MuiGrid container spacing={3}>
                        <MuiGrid item xs={12} md={6}>
                          <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                              Role Performance
                            </Typography>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Games</TableCell>
                                    <TableCell>Wins</TableCell>
                                    <TableCell>Win Rate</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  <TableRow>
                                    <TableCell>
                                      <Chip icon={<PeopleIcon />} label="Citizen" color="primary" size="small" />
                                    </TableCell>
                                    <TableCell>{playerStats.civilian_games}</TableCell>
                                    <TableCell>{playerStats.civilian_wins}</TableCell>
                                    <TableCell>
                                      {playerStats.civilian_games > 0 
                                        ? ((playerStats.civilian_wins / playerStats.civilian_games) * 100).toFixed(1) + '%' 
                                        : '0%'}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>
                                      <Chip icon={<LocalPoliceIcon />} label="Sheriff" color="success" size="small" />
                                    </TableCell>
                                    <TableCell>{playerStats.sheriff_games}</TableCell>
                                    <TableCell>{playerStats.sheriff_wins}</TableCell>
                                    <TableCell>
                                      {playerStats.sheriff_games > 0 
                                        ? ((playerStats.sheriff_wins / playerStats.sheriff_games) * 100).toFixed(1) + '%' 
                                        : '0%'}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>
                                      <Chip icon={<SportsKabaddiIcon />} label="Mafia" color="error" size="small" />
                                    </TableCell>
                                    <TableCell>{playerStats.mafia_games}</TableCell>
                                    <TableCell>{playerStats.mafia_wins}</TableCell>
                                    <TableCell>
                                      {playerStats.mafia_games > 0 
                                        ? ((playerStats.mafia_wins / playerStats.mafia_games) * 100).toFixed(1) + '%' 
                                        : '0%'}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>
                                      <Chip icon={<TheaterComedyIcon />} label="Don" color="warning" size="small" />
                                    </TableCell>
                                    <TableCell>{playerStats.don_games}</TableCell>
                                    <TableCell>{playerStats.don_wins}</TableCell>
                                    <TableCell>
                                      {playerStats.don_games > 0 
                                        ? ((playerStats.don_wins / playerStats.don_games) * 100).toFixed(1) + '%' 
                                        : '0%'}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                            
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Average Bonus Points: <strong>{playerStats.avg_bonus_points}</strong>
                              </Typography>
                            </Box>
                          </Paper>
                        </MuiGrid>
                        
                        <MuiGrid item xs={12} md={6}>
                          <Paper sx={{ p: 3, height: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6">
                                Recent Games
                              </Typography>
                              <Box>
                                <IconButton 
                                  size="small" 
                                  onClick={toggleFilters} 
                                  color={filtersOpen || leagueFilter ? "primary" : "default"}
                                  title="Toggle filters"
                                >
                                  <FilterAltIcon />
                                </IconButton>
                              </Box>
                            </Box>
                            
                            {filtersOpen && (
                              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.12)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                  <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <Select
                                      value={leagueFilter}
                                      onChange={handleLeagueFilterChange as any}
                                      displayEmpty
                                    >
                                      <MenuItem value="">All Leagues</MenuItem>
                                      {availableLeagues.map(league => (
                                        <MenuItem key={league.id} value={league.id}>
                                          {league.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  
                                  {(leagueFilter) && (
                                    <Button 
                                      variant="outlined" 
                                      size="small" 
                                      startIcon={<ClearIcon />}
                                      onClick={clearFilters}
                                    >
                                      Clear Filters
                                    </Button>
                                  )}
                                </Box>
                                {allGames.length > 0 && (
                                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                    Showing {filteredGames.length} of {allGames.length} games
                                  </Typography>
                                )}
                              </Box>
                            )}
                            
                            {allGames && allGames.length > 0 ? (
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Game</TableCell>
                                      <TableCell>League</TableCell>
                                      <TableCell>Role</TableCell>
                                      <TableCell>Result</TableCell>
                                      <TableCell>Bonus</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {recentGames.map((result) => (
                                      <TableRow 
                                        key={result.id}
                                        hover
                                        onClick={() => navigate(`/games?game=${result.game.id}`)}
                                        sx={{ 
                                          cursor: 'pointer',
                                          '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.08)'
                                          }
                                        }}
                                      >
                                        <TableCell>
                                          <Box>
                                            <Typography variant="body2">
                                              Session {result.game.session_num}, Game {result.game.number_in_session}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {formatDate(result.game.occurred_at)}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          {(result.game.league as any)?.name || 'Unknown'}
                                        </TableCell>
                                        <TableCell>
                                          {getRoleChip(result.role)}
                                        </TableCell>
                                        <TableCell>
                                          <Stack direction="column" spacing={1}>
                                            {getResultChip(result.is_win)}
                                            {result.is_killed_first && (
                                              <Chip
                                                  label={"1st"}
                                                icon={<GpsFixedIcon />}
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                sx={{ 
                                                  mt: 0.5,
                                                  height: 24
                                                }}
                                              />
                                            )}
                                          </Stack>
                                        </TableCell>
                                        <TableCell>
                                          <Stack direction="column" spacing={1} alignItems="flex-start">
                                            {result.bonus_points !== 0 && (
                                              <Chip 
                                                label={`${result.bonus_points > 0 ? '+' : ''}${result.bonus_points}`}
                                                size="small"
                                                color={result.bonus_points > 0 ? "success" : "error"}
                                              />
                                            )}
                                            {getWinningMoveBonus(result)}
                                          </Stack>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow>
                                      <TableCell colSpan={5} align="center">
                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                          <Pagination 
                                            count={totalPages} 
                                            page={page} 
                                            onChange={handlePageChange} 
                                            color="primary" 
                                            size="small"
                                          />
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                              </TableContainer>
                            ) : filteredGames.length === 0 && leagueFilter ? (
                              <Typography variant="body2" color="text.secondary">
                                No games found with the current filters. Try changing your filters.
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No games found.
                              </Typography>
                            )}
                          </Paper>
                        </MuiGrid>
                      </MuiGrid>
                    </>
                  )
                )}
              </>
            ) : (
              <Typography variant="h5" color="error">
                Player not found
              </Typography>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default PlayerProfile;